import { useState, useEffect } from "react";
import { createModel } from "hox";
import { TablePaginationConfig } from "antd/es/table/interface";
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE, defaultMdCmParam } from "@/utils/constants";
import { IMaterialDetail, IMaterialSearchFields, materialService, IMaterialInsert, IMaterialUpdate } from "./materialService";
import { globalPrompt } from "@/components/message";
import { useGlobalModel, transPgToPagination } from "@/model/globalModel";
import { convertRes2Blob } from "@/utils/exportFile";
import { ISearchPage } from "@/types/AppTypes";
import { switchNum } from "@/utils/utils";
import moment from "moment";

export const useMaterialModel = createModel(function () {
    const { user: { shopCode, shopName, userName }, setLoadingInfo } = useGlobalModel(({ user }) => [user]);
    const { sourceApp, branch } = defaultMdCmParam;
    /**
     * 列表
     */
    //列表数据源
    const [dataSource, setDataSource] = useState<IMaterialDetail[]>([]);
    //查询条件
    const [searchParam, setSearchParam] = useState<IMaterialSearchFields>({});
    //分页参数
    const initPgCfg: TablePaginationConfig = { pageSize: DEFAULT_PAGE_SIZE, current: DEFAULT_FIRST_PAGE_NUM }
    const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);
    //表格多选id值
    const [selectedRowKeys, setSelectedRowKeys] = useState<IMaterialDetail[]>([]);
    //门店共享
    const [shareShop, serShareShop] = useState<boolean>(false);


    //列表查询
    const loadData = async (page: ISearchPage = initPgCfg, searchValue: IMaterialSearchFields = searchParam) => {
        const searchParams = {
            shopCode,
            ...searchValue,
            pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
            pageNum: (page?.current || page?.pageNum) ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
        }
        let { retData } = await materialService.page(searchParams);
        const { records, ...pg } = retData;
        setPgCfg(transPgToPagination(pg));
        setDataSource(records);
    }
    //设置搜索value
    const updateSearchParam = (updateParam: IMaterialSearchFields) => {
        setSearchParam({ ...searchParam, ...updateParam })
    }

    //重置搜索参数
    const resetSearchParam = () => {
        setSearchParam({});
        setSelectedRowKeys([]);
        loadData(initPgCfg, {});
    }

    /** 
     * 表单详情
     */
    const initAddFormVal = {
        id: "",
        shopName,
        createUser: userName,
        status: true
    }
    //编辑表单缓存
    const [editFormVal, setEditFormVal] = useState<IMaterialDetail>(initAddFormVal);
    //新增表单缓存
    const [addFormVal, setAddFormVal] = useState<IMaterialDetail>(initAddFormVal);
    //表单新增修改
    const updateAddFormVal = (val: { [key: string]: any } | null) => {
        val ? setAddFormVal({ ...addFormVal, ...val }) : setAddFormVal(initAddFormVal);
    }
    //表单新增修改
    const updateEditFormVal = (val: { [key: string]: any } | null) => {
        val ? setEditFormVal({ ...editFormVal, ...val }) : setEditFormVal(initAddFormVal);
    }
    //获取一条详情数据 Api
    const getMaterialOne = async (id: string) => {
        let { retData } = await materialService.one(id, shopCode);
        //const {list} =retData
        setEditFormVal({
            ...retData,
            status: +(retData.status ?? 0) ? true : false,
            //createTime:retData.createTime instanceof Object ?  retData.createTime.format('YYYY-MM-DD HH:mm:ss') :null,
            createTime:retData.createTime? moment(retData.createTime) :null,
            updateTime: retData.updateTime? moment(retData.updateTime) :null,
        });
        return retData;
    }

    //表单新增 Api
    const insertMaterial = async (val: IMaterialDetail) => {
        let { status, ...params } = val;
        let data: IMaterialDetail = {
            ...params,
            sourceApp, branch,
            shopCode, shopName,
            status: status ? '1' : '0',
            createTime:params.createTime instanceof Object ?  params.createTime.format('YYYY-MM-DDTHH:mm:ss') :null,
            updateTime:params.updateTime instanceof Object ?  params.updateTime.format('YYYY-MM-DDTHH:mm:ss') :null,
        }
        let { retData } = await materialService.insert(data);
        globalPrompt("message", { text: "商品新增成功", type: "success" });
        return retData;
    }

    //表单编辑 Api
    const editMaterial = async (val: any & { id: string }) => {
        let { status, ...params } = val;
        let data: IMaterialDetail = {
            ...params,
            sourceApp, branch,
            shopCode, shopName,
            status: status ? '1' : '0',
            createTime:params.createTime instanceof Object ?  params.createTime.format('YYYY-MM-DDTHH:mm:ss') :null,
            updateTime:params.updateTime instanceof Object ?  params.updateTime.format('YYYY-MM-DDTHH:mm:ss') :null,
        }
        let { retData } = await materialService.update(data);
        globalPrompt("message", { text: "商品编辑成功", type: "success" });
        return retData;
    }


    //导出
    const exportMaterial = async (searchValue: IMaterialSearchFields = searchParam) => {
        setLoadingInfo({ loadingVisible: true, txt: "快速导出中" });
        try {
            const searchParams = {
                shopCode,
                ...searchValue,
                id: selectedRowKeys.map(item => item.id)?.join(),
                pageSize: -1,
                pageNum: 1,
            };

            let result = await materialService.export(searchParams);
            result && setLoadingInfo({ loadingVisible: false, txt: "" });
            convertRes2Blob(result);
        } catch { setLoadingInfo({ loadingVisible: false, txt: "" }) }
    }

    //下载模板
    const downloadTpl = async () => {
        let result = await materialService.downloadTpl();
        convertRes2Blob(result);
    }

    //启用 禁用 api
    const disableMaterial = async (id: string, status: number) => {
        let { retData } = await materialService.disable(id, status, shopCode);
        loadData()
        globalPrompt("message", { text: "操作成功", type: "success" })
    };
    return {
        /***field***/
        searchParam,
        dataSource,
        selectedRowKeys,
        pgCfg,
        editFormVal,
        addFormVal,
        shareShop, serShareShop,
        /***method***/
        updateAddFormVal,
        updateEditFormVal,
        insertMaterial,
        editMaterial,
        getMaterialOne,
        setDataSource,
        setSelectedRowKeys,
        resetSearchParam,
        updateSearchParam,
        loadData,
        exportMaterial,
        downloadTpl,
        disableMaterial,
    }
});